import { useEffect, useState } from "react";
import { api, fmtEur } from "../lib/api";
import { useApp } from "../lib/store";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const NAVY = "#1B2A4A", AMBER = "#F5A623";
const PIE_COLORS = [NAVY, AMBER, "#3B82F6", "#10B981", "#8B5CF6", "#EF4444", "#64748B"];

function Kpi({ label, value, sub, testid, accent, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      whileHover={{ y: -3 }}
      className="bg-white border border-gray-200 rounded-xl p-5 card-hover" data-testid={testid}>
      <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{label}</p>
      <p className={`text-2xl font-bold mt-1 font-mono ${accent ? "text-[#F5A623]" : "text-gray-900"}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </motion.div>
  );
}

function Card({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl p-5 card-hover ${className}`}>
      <h3 className="text-sm font-semibold text-gray-700 mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { t, user, lang, picklists, plLabel } = useApp();
  const navigate = useNavigate();
  const [sales, setSales] = useState(null);
  const [quality, setQuality] = useState(null);
  const [mgmt, setMgmt] = useState(null);
  const [custFc, setCustFc] = useState(null);
  const [ibStats, setIbStats] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [segment, setSegment] = useState("all");

  useEffect(() => {
    const q = segment === "all" ? "" : `?segment=${encodeURIComponent(segment)}`;
    const cfSep = segment === "all" ? "" : `&segment=${encodeURIComponent(segment)}`;
    api.get(`/dashboard/sales${q}`).then((r) => setSales(r.data)).catch(() => {});
    api.get(`/dashboard/quality${q}`).then((r) => setQuality(r.data)).catch(() => {});
    api.get(`/dashboard/management${q}`).then((r) => setMgmt(r.data)).catch(() => {});
    api.get(`/dashboard/customer-forecast?limit=8${cfSep}`).then((r) => setCustFc(r.data)).catch(() => {});
  }, [segment]);

  useEffect(() => {
    api.get("/ideas/stats").then((r) => setIbStats(r.data)).catch(() => {});
    api.get("/roadmap/dashboard-widget").then((r) => setRoadmap(r.data)).catch(() => {});
  }, []);

  const defaultTab = user?.role === "customer_service" ? "quality" : user?.role === "read_only" ? "management" : "sales";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("greeting_morning") : hour < 18 ? t("greeting_afternoon") : t("greeting_evening");
  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1B2A4A] via-[#22345c] to-[#2f4a7a] text-white p-7 grain" data-testid="dashboard-hero">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#F5A623]/25 blur-3xl animate-float-slow" />
        <div className="absolute right-40 bottom-0 h-40 w-40 rounded-full bg-[#3B82F6]/20 blur-3xl animate-float-slower" />
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative">
          <p className="text-[#F5A623] text-sm font-semibold tracking-wide">{greeting}, {firstName}</p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>{t("dashboard")}</h1>
          <p className="text-white/60 text-sm mt-2">{t("todays_overview")} · {new Date().toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </motion.div>
        <div className="relative mt-4 flex items-center gap-2" data-testid="dashboard-segment-filter-wrap">
          <span className="text-white/70 text-xs font-semibold uppercase tracking-wide">{t("segment")}</span>
          <Select value={segment} onValueChange={setSegment}>
            <SelectTrigger className="w-60 bg-white/10 border-white/20 text-white hover:bg-white/15" data-testid="dashboard-segment-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("all")}</SelectItem>
              {(picklists.segments || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="sales" data-testid="dash-tab-sales">{t("sales_dash")}</TabsTrigger>
          <TabsTrigger value="quality" data-testid="dash-tab-quality">{t("quality_dash")}</TabsTrigger>
          <TabsTrigger value="management" data-testid="dash-tab-management">{t("management_dash")}</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="space-y-6 mt-4">
          {sales && (<>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Kpi label={t("total_pipeline")} value={fmtEur(sales.total_pipeline)} testid="kpi-pipeline" accent index={0} />
              <Kpi label={t("weighted_pipeline")} value={fmtEur(sales.weighted_pipeline)} testid="kpi-weighted" index={1} />
              <Kpi label={t("win_rate")} value={`${sales.win_rate}%`} testid="kpi-winrate" index={2} />
              <Kpi label={t("avg_deal")} value={fmtEur(sales.avg_deal_size)} testid="kpi-avgdeal" index={3} />
              <Kpi label={t("aging_alert")} value={sales.aging_count} sub={`${sales.open_count} ${t("open_opps")}`} testid="kpi-aging" index={4} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title={t("pipeline_by_stage")}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={sales.pipeline_by_stage.map((d) => ({ ...d, stage: plLabel("opp_stages", d.stage) }))} layout="vertical" margin={{ left: 30 }}>
                    <XAxis type="number" tickFormatter={(v) => `${v / 1000}k`} fontSize={11} />
                    <YAxis type="category" dataKey="stage" width={90} fontSize={11} />
                    <Tooltip formatter={(v) => fmtEur(v)} />
                    <Bar dataKey="value" fill={NAVY} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("forecast_by_month")}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={sales.forecast_by_month}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis tickFormatter={(v) => `${v / 1000}k`} fontSize={11} />
                    <Tooltip formatter={(v) => fmtEur(v)} />
                    <Bar dataKey="value" fill={AMBER} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("forecast_by_rep")}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={sales.forecast_by_rep} layout="vertical" margin={{ left: 40 }}>
                    <XAxis type="number" tickFormatter={(v) => `${v / 1000}k`} fontSize={11} />
                    <YAxis type="category" dataKey="rep" width={100} fontSize={11} />
                    <Tooltip formatter={(v) => fmtEur(v)} />
                    <Bar dataKey="value" fill={NAVY} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("revenue_by_segment")}>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={sales.revenue_by_segment} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {sales.revenue_by_segment.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtEur(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 text-xs mt-2">
                  {sales.revenue_by_segment.map((s, i) => (
                    <span key={s.name} className="flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{s.name}
                    </span>
                  ))}
                </div>
              </Card>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <Card title={t("pipeline_by_family")} className="md:col-span-2">
                {sales.pipeline_by_family?.length ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={sales.pipeline_by_family} layout="vertical" margin={{ left: 40 }}>
                      <XAxis type="number" tickFormatter={(v) => fmtEur(v)} hide />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => fmtEur(v)} />
                      <Bar dataKey="value" fill="#1B2A4A" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-gray-400 py-12 text-center">{t("ai_no_data")}</p>}
              </Card>
              <Card title={t("external_sourcing_share")}>
                <div className="flex flex-col items-center justify-center h-[200px]">
                  <span className="text-5xl font-bold font-mono text-[#F5A623]" data-testid="external-share-pct">{sales.external_share_pct ?? 0}%</span>
                  <p className="text-xs text-gray-400 mt-3 text-center px-4">{t("external_sourcing_help")}</p>
                </div>
              </Card>
            </div>
            <Card title={t("top_opportunities")}>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase text-gray-400 border-b">
                  <th className="py-2">{t("name")}</th><th>{t("account")}</th><th>{t("stage")}</th><th>{t("owner")}</th><th className="text-right">{t("amount")}</th>
                </tr></thead>
                <tbody>
                  {sales.top_opportunities.map((o) => (
                    <tr key={o.id} className="border-b border-gray-50 hover:bg-amber-50/40 cursor-pointer" onClick={() => navigate(`/opportunities?id=${o.id}`)}>
                      <td className="py-2 font-medium">{o.name}</td><td>{o.account_name}</td>
                      <td><Badge variant="outline">{plLabel("opp_stages", o.stage)}</Badge></td><td>{o.owner_name}</td>
                      <td className="text-right font-mono">{fmtEur(o.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>)}
        </TabsContent>

        <TabsContent value="quality" className="space-y-6 mt-4">
          {quality && (<>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Kpi label={t("open_complaints")} value={quality.open_count} testid="kpi-open-complaints" accent />
              <Kpi label={t("critical")} value={quality.critical_count} testid="kpi-critical" />
              <Kpi label={t("claimed_value")} value={fmtEur(quality.total_claimed)} testid="kpi-claimed" />
              <Kpi label={t("avg_resolution")} value={quality.avg_resolution_days} testid="kpi-resolution" />
              <Kpi label={t("sla_compliance")} value={`${quality.sla_compliance}%`} testid="kpi-sla" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card title={t("pareto")}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={quality.pareto.slice(0, 8).map((d) => ({ ...d, name: plLabel("defect_categories", d.name) }))} margin={{ bottom: 60 }}>
                    <XAxis dataKey="name" fontSize={9} angle={-35} textAnchor="end" interval={0} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill={NAVY} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("trend_12m")}>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={quality.trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" fontSize={11} />
                    <YAxis fontSize={11} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke={AMBER} strokeWidth={2.5} dot={{ fill: NAVY }} />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("by_severity")}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={quality.by_severity} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75}>
                      {quality.by_severity.map((s, i) => (
                        <Cell key={i} fill={s.name === "Critical" ? "#EF4444" : s.name === "Major" ? AMBER : "#94A3B8"} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-3 text-xs justify-center">
                  {quality.by_severity.map((s) => <span key={s.name}>{s.name}: <b>{s.value}</b></span>)}
                </div>
              </Card>
              <Card title={t("top_accounts_complaints")}>
                {quality.top_accounts.map((a) => (
                  <div key={a.name} className="flex justify-between py-2 border-b border-gray-50 text-sm">
                    <span>{a.name}</span><span className="font-mono">{fmtEur(a.value)}</span>
                  </div>
                ))}
              </Card>
            </div>
          </>)}
        </TabsContent>

        <TabsContent value="management" className="space-y-6 mt-4">
          {mgmt && (<>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Kpi label={t("revenue_ytd")} value={fmtEur(mgmt.revenue_ytd)} sub={`${mgmt.yoy_pct > 0 ? "+" : ""}${mgmt.yoy_pct}% ${t("vs_py")}`} testid="kpi-revenue-ytd" accent />
              <Kpi label={t("vs_py")} value={fmtEur(mgmt.revenue_py)} testid="kpi-revenue-py" />
              <Kpi label={t("accounts")} value={mgmt.account_count} testid="kpi-accounts" />
              <Kpi label="Closed Won" value={fmtEur(mgmt.won_total)} testid="kpi-won" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card title={t("abc_distribution")}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={mgmt.abc_distribution} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} label>
                      {mgmt.abc_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("business_split")}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={mgmt.business_split}>
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis tickFormatter={(v) => `${v / 1000}k`} fontSize={11} />
                    <Tooltip formatter={(v) => fmtEur(v)} />
                    <Bar dataKey="value" fill={AMBER} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title={t("churn_risk")}>
                <div className="space-y-1 max-h-52 overflow-y-auto">
                  {mgmt.churn_risk.map((a) => (
                    <button key={a.id} onClick={() => navigate(`/accounts/${a.id}`)}
                      className="flex justify-between w-full py-1.5 border-b border-gray-50 text-sm hover:bg-amber-50/40 px-1 transition-colors duration-100">
                      <span className="flex items-center gap-2">
                        <Badge className="bg-red-100 text-red-600 hover:bg-red-100">{a.abc_class}</Badge>{a.name}
                      </span>
                      <span className="font-mono text-gray-500">{fmtEur(a.annual_spend)}</span>
                    </button>
                  ))}
                </div>
              </Card>
            </div>
            {custFc && custFc.customers.length > 0 && (
              <Card title={`${t("top_customer_forecast")} · ${custFc.year}`}>
                <div className="space-y-3" data-testid="dash-customer-forecast">
                  {custFc.customers.map((c) => {
                    const max = custFc.customers[0].fc_revenue || 1;
                    return (
                      <button key={c.account_id} onClick={() => navigate(`/accounts/${c.account_id}`)} data-testid="dash-cf-row"
                        className="w-full text-left group">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium truncate">{c.name}</span>
                          <span className="font-mono text-xs text-gray-500">{fmtEur(c.actual_revenue)} / {fmtEur(c.fc_revenue)}
                            <span className={`ml-2 font-semibold ${c.attainment >= 100 ? "text-emerald-600" : c.attainment >= 60 ? "text-[#F5A623]" : "text-red-500"}`}>{c.attainment}%</span>
                          </span>
                        </div>
                        <div className="relative h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                          <div className="h-full bg-[#F5A623]" style={{ width: `${Math.min((c.fc_approved / max) * 100, 100)}%` }} title={t("approved_fc")} />
                          <div className="h-full bg-blue-400" style={{ width: `${Math.min((c.fc_pending / max) * 100, 100)}%` }} title={t("pending_fc")} />
                        </div>
                      </button>
                    );
                  })}
                  <div className="flex gap-4 text-[11px] text-gray-500 pt-1">
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#F5A623]" />{t("approved_fc")}</span>
                    <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-blue-400" />{t("pending_fc")}</span>
                  </div>
                </div>
              </Card>
            )}
            {ibStats && (
              <Card title={t("ib_dashboard_title")}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="dash-ideas-bugs">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("ib_open_bugs_severity")}</p>
                    <ResponsiveContainer width="100%" height={160}>
                      <BarChart data={ibStats.open_bugs_by_severity}>
                        <XAxis dataKey="name" fontSize={10} />
                        <YAxis fontSize={11} allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {ibStats.open_bugs_by_severity.map((s, i) => (
                            <Cell key={i} fill={s.name === "Critical" ? "#EF4444" : s.name === "Major" ? AMBER : s.name === "Minor" ? "#94A3B8" : "#CBD5E1"} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("ib_top_ideas")}</p>
                    <div className="space-y-1.5">
                      {ibStats.top_ideas.length === 0 && <p className="text-xs text-gray-400">–</p>}
                      {ibStats.top_ideas.map((it, i) => (
                        <button key={it.id} onClick={() => navigate(`/ideas/${it.id}`)} data-testid="dash-top-idea"
                          className="flex items-center justify-between w-full text-sm py-1 hover:bg-amber-50/40 px-1 rounded transition-colors duration-100">
                          <span className="truncate flex items-center gap-2"><span className="text-gray-400 font-mono text-xs">{i + 1}.</span>{it.title}</span>
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 font-mono">{it.votes}</Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col justify-center gap-3">
                    <button onClick={() => navigate("/ideas")} className="rounded-lg border border-gray-100 p-3 text-left hover:bg-amber-50/40 transition-colors duration-100">
                      <p className="text-2xl font-bold font-mono text-[#1B2A4A]">{ibStats.opened_30d}</p>
                      <p className="text-xs text-gray-500">{t("ib_opened_30d")}</p>
                    </button>
                    <button onClick={() => navigate("/ideas")} className="rounded-lg border border-gray-100 p-3 text-left hover:bg-amber-50/40 transition-colors duration-100">
                      <p className="text-2xl font-bold font-mono text-emerald-600">{ibStats.closed_30d}</p>
                      <p className="text-xs text-gray-500">{t("ib_closed_30d")}</p>
                    </button>
                  </div>
                </div>
              </Card>
            )}
            {roadmap && (
              <Card className="p-5" data-testid="dash-roadmap">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold text-[#1B2A4A]">{t("roadmap_widget_title")}</p>
                  <button onClick={() => navigate("/roadmap")} className="text-xs text-blue-600 hover:underline">{t("roadmap")} →</button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("roadmap_top_voted")}</p>
                    <div className="space-y-1.5">
                      {roadmap.top_voted.length === 0 && <p className="text-xs text-gray-400">–</p>}
                      {roadmap.top_voted.map((it, i) => (
                        <button key={it.id} onClick={() => navigate(`/roadmap/${it.id}`)} data-testid="dash-roadmap-top"
                          className="flex items-center justify-between w-full text-sm py-1 hover:bg-amber-50/40 px-1 rounded transition-colors duration-100">
                          <span className="truncate flex items-center gap-2"><span className="text-gray-400 font-mono text-xs">{i + 1}.</span>{it.title}</span>
                          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 font-mono">{it.votes}</Badge>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("roadmap_my_votes")}</p>
                    <div className="space-y-1">
                      {roadmap.my_votes.length === 0 && <p className="text-xs text-gray-400">–</p>}
                      {roadmap.my_votes.map((it) => (
                        <button key={it.id} onClick={() => navigate(`/roadmap/${it.id}`)}
                          className="flex items-center justify-between w-full text-sm py-0.5 hover:bg-amber-50/40 px-1 rounded truncate">
                          <span className="truncate">{it.title}</span>
                          <span className="text-[10px] text-gray-400 ml-2 shrink-0">{it.status}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold mb-2">{t("roadmap_my_subs")}</p>
                    <div className="space-y-1">
                      {roadmap.my_submissions.length === 0 && <p className="text-xs text-gray-400">–</p>}
                      {roadmap.my_submissions.map((it) => (
                        <button key={it.id} onClick={() => navigate(`/roadmap/${it.id}`)}
                          className="flex items-center justify-between w-full text-sm py-0.5 hover:bg-amber-50/40 px-1 rounded truncate">
                          <span className="truncate">{it.title}</span>
                          <span className="text-[10px] text-gray-400 ml-2 shrink-0">{it.status}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </>)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
